import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getAvailableJobs, listTrades, type Trade } from "../../lib/api/contractor";
import { ApiError } from "../../lib/api/client";
import { useContractor } from "../../lib/auth";
import { useGeolocation } from "../../lib/useGeolocation";
import {
  LAUNCH_TRADES,
  jobCategoryDisplay,
  parseJobCategory,
} from "../../lib/types/enums";
import type { AvailableJobsResponse } from "../../lib/types/models";
import { JobCard } from "../../components/JobCard";
import { Banner, ChipToggle, EmptyState, ErrorState, PageHead, SkeletonList, TradeIcon } from "@ui/react";
import { CalendarIcon, SearchIcon, ShieldIcon } from "@ui/react/icons";
import { errorMessage } from "../../lib/errorMessage";
import { routerLink } from "../../lib/routerLink";

/**
 * The marketplace feed.
 *
 * `GET /jobs/available` has four outcomes, not two, and each needs its own
 * words: a 403 NOT_VERIFIED (account still under review), a `blocked: true`
 * body (verified but paused from new work), a `mode: "bookable"` body (this
 * contractor is not on the marketplace at all), and an ordinary feed that may
 * legitimately be empty.
 *
 * There is deliberately no "near me" FILTER — `GET /jobs/nearby` exists, but
 * this feed is every open job in the contractor's trades, unnarrowed. The
 * browser location fix below is used only to compute the "X km away" shown on
 * each card (W09-4); it is never sent to `PATCH /profile/location`, so it
 * cannot overwrite the phone app's real GPS fix or change what its own nearby
 * feed returns.
 */
export function FindJobsScreen() {
  const contractor = useContractor();
  const geo = useGeolocation();
  const [state, setState] = useState<{
    loading: boolean;
    error: unknown;
    data: AvailableJobsResponse | null;
  }>({ loading: true, error: null, data: null });
  const [category, setCategory] = useState<string | null>(null);

  // Only the empty-skills fallback below is remote-or-bundled; a contractor's
  // OWN skills are never narrowed against this list. Starts as LAUNCH_TRADES
  // and swaps for GET /categories once it resolves; `listTrades` never rejects.
  // Whole rows, not just values: a row also carries the remote `icon` a trade
  // with no bundled drawing renders with, and that is needed for a chip the
  // contractor's OWN skills produced too.
  const [remoteTrades, setRemoteTrades] = useState<Trade[]>(() =>
    LAUNCH_TRADES.map((trade) => ({ value: trade, label: jobCategoryDisplay(trade) })),
  );
  useEffect(() => {
    void listTrades().then(setRemoteTrades);
  }, []);
  const iconFor = (value: string) => remoteTrades.find((trade) => trade.value === value)?.icon;

  // Guards against a slow no-position response painting over the position-fixed
  // one that follows it a moment later (see JobDetailsScreen's requestSeq).
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const seq = ++requestSeq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getAvailableJobs(geo.position?.lat, geo.position?.lng);
      if (seq !== requestSeq.current) return;
      setState({ loading: false, error: null, data });
    } catch (error) {
      if (seq !== requestSeq.current) return;
      setState({ loading: false, error, data: null });
    }
  }, [geo.position]);

  useEffect(() => {
    void load();
  }, [load]);

  // Chips are scoped to the trades this contractor actually works. A skill that
  // doesn't parse (an old value, a typo) still gets a chip rather than
  // vanishing — same bug category_filter_chips.dart had on mobile. Falling
  // back to the four launch trades keeps the filter usable for an account
  // whose skills list is somehow empty, rather than rendering a lone "All jobs".
  const categories = useMemo<string[]>(() => {
    const own = (contractor.skills ?? []).map((skill) => parseJobCategory(skill) ?? skill);
    const unique = Array.from(new Set(own));
    return unique.length > 0 ? unique : remoteTrades.map((trade) => trade.value);
  }, [contractor.skills, remoteTrades]);

  const jobs = state.data?.jobs ?? [];
  // `?? job.category` for the same reason as `?? skill` above: a trade that is
  // enabled in config/trades but not bundled here parses to null, and without
  // the fallback its own chip filtered every one of its jobs out.
  const visible = category
    ? jobs.filter((job) => (parseJobCategory(job.category) ?? job.category) === category)
    : jobs;

  const blocked = state.data?.blocked === true;
  const bookable = state.data?.mode === "bookable";
  const unverified = state.error instanceof ApiError && state.error.isUnverified;

  return (
    <div className="stack stack--loose">
      {/* The count is deliberately absent when the feed is empty: it used to
          render "0 jobs available" here and "No jobs available" again 200px
          lower, with the trade chips still sitting over nothing — three
          elements describing one absence. */}
      <PageHead
        eyebrow="Find jobs"
        title="Work near you"
        lead={
          !state.loading && !state.error && !bookable && visible.length > 0
            ? `${visible.length} job${visible.length === 1 ? "" : "s"} available${
                category ? ` in ${jobCategoryDisplay(category)}` : ""
              }`
            : undefined
        }
      />

      {unverified ? (
        <EmptyState
          icon={<ShieldIcon />}
          title="Your account is under review"
          action={{ label: "Check again", onClick: () => void load() }}
        >
          We're verifying your details. As soon as that's done, jobs in your trades
          and service areas will show up here.
        </EmptyState>
      ) : state.error ? (
        <ErrorState message={errorMessage(state.error)} onRetry={() => void load()} />
      ) : state.loading ? (
        <div role="status">
          <span className="sr-only">Finding jobs</span>
          <SkeletonList rows={4} height="8rem" />
        </div>
      ) : bookable ? (
        <EmptyState
          icon={<CalendarIcon />}
          title="You take direct bookings"
          action={{ label: "Go to bookings", href: "/bookings" }}
          renderLink={routerLink}
        >
          Your account is set up for direct bookings rather than the marketplace, so
          there is no job feed to browse. Customers book you from your own calendar
          instead.
        </EmptyState>
      ) : (
        <>
          {blocked ? (
            <Banner tone="warn" title="New jobs are paused">
              Your account is under review, so the feed is on hold. Our team will be
              in touch — work already in progress is unaffected.
            </Banner>
          ) : null}

          {/* No filters over an empty feed — the only useful control there is
              the empty state's own Refresh. */}
          {jobs.length > 0 ? (
          <div className="chip-scroller">
            <ChipToggle pressed={category === null} onToggle={() => setCategory(null)}>
              All jobs
            </ChipToggle>
            {categories.map((option) => (
              <ChipToggle
                key={option}
                pressed={category === option}
                onToggle={() => setCategory(option)}
              >
                <TradeIcon trade={option} pathBody={iconFor(option)} />
                {jobCategoryDisplay(option)}
              </ChipToggle>
            ))}
          </div>
          ) : null}

          {visible.length > 0 ? (
            <div className="stack">
              {visible.map((job) => (
                <JobCard key={job.jobId} job={job} href={`/jobs/${job.jobId}`} />
              ))}
            </div>
          ) : blocked ? (
            // The banner above already explains the empty feed; a "check back
            // soon" state here would contradict it.
            null
          ) : category ? (
            <EmptyState
              icon={<SearchIcon />}
              title={`No ${jobCategoryDisplay(category)} jobs right now`}
              action={{ label: "Show all jobs", onClick: () => setCategory(null) }}
            >
              Nothing in this trade at the moment. Other trades may still have work.
            </EmptyState>
          ) : (
            <EmptyState
              icon={<SearchIcon />}
              title="No jobs available"
              action={{ label: "Refresh", onClick: () => void load() }}
            >
              New work appears here as homeowners post it. Check back soon.
            </EmptyState>
          )}
        </>
      )}
    </div>
  );
}
